import { Badge, Stack, Typography, toast } from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  deriveReferralCodeStatus,
  listReferralCodes,
  type ListedReferralCode,
  type ReferralCodeStatus,
} from "../../services/referrals";
import { Loader } from "../../components/Loader";

dayjs.extend(relativeTime);

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const expiryToneClass = (iso: string): string => {
  const diff = dayjs(iso).diff(dayjs());
  if (diff <= 0) return "al-text-red-600";
  if (diff <= SEVEN_DAYS_MS) return "al-text-amber-600";
  return "al-text-gray-700";
};

const STATUS_LABEL: Record<ReferralCodeStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  expired: "Expired",
  exhausted: "Exhausted",
};

const STATUS_BADGE_CLASS: Record<ReferralCodeStatus, string> = {
  active: "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200",
  inactive: "!al-bg-gray-100 !al-text-gray-700 al-border al-border-gray-200",
  expired: "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200",
  exhausted: "!al-bg-amber-100 !al-text-amber-800 al-border al-border-amber-200",
};

const copyToClipboard = async (code: string) => {
  try {
    await navigator.clipboard.writeText(code);
    toast({ description: `Copied ${code}` });
  } catch {
    toast({ description: "Could not copy to clipboard", variant: "destructive" });
  }
};

export const ReferralCodesTable = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["referralCodes"],
    queryFn: listReferralCodes,
  });

  if (isLoading) return <Loader label="Loading referral codes..." />;

  if (error) {
    return (
      <Typography variant="body" className="al-text-red-600">
        Failed to load referral codes.
      </Typography>
    );
  }

  const codes: ListedReferralCode[] = Array.isArray(data) ? data : [];

  if (codes.length === 0) {
    return (
      <Stack direction="column" gap="sm" align="center" className="al-py-12">
        <Typography variant="body" className="al-text-muted-foreground">
          No referral codes yet — create one.
        </Typography>
      </Stack>
    );
  }

  const now = new Date();

  return (
    <div
      role="region"
      aria-label="Referral codes"
      className="al-border al-border-gray-200 al-rounded-lg al-overflow-hidden"
    >
      <table className="al-w-full al-border-collapse">
        <thead className="al-bg-gray-50">
          <tr>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Code
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Status
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Used / Max
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Remaining
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Expires at
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Created by
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
              Created at
            </th>
          </tr>
        </thead>
        <tbody className="al-bg-white">
          {codes.map(code => {
            const status = deriveReferralCodeStatus(code, now);
            return (
              <tr key={code.id} className="al-border-b al-border-gray-200">
                <td className="al-px-4 al-py-3">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(code.code)}
                    className="al-font-mono al-text-sm hover:al-underline"
                    aria-label={`Copy ${code.code}`}
                  >
                    {code.code}
                  </button>
                </td>
                <td className="al-px-4 al-py-3">
                  <Badge className={STATUS_BADGE_CLASS[status]}>{STATUS_LABEL[status]}</Badge>
                </td>
                <td className="al-px-4 al-py-3 al-text-sm al-text-gray-700">
                  {code.used_count} / {code.max_uses ?? "∞"}
                </td>
                <td className="al-px-4 al-py-3 al-text-sm al-text-gray-700">
                  {code.remaining_uses ?? "∞"}
                </td>
                <td
                  className={`al-px-4 al-py-3 al-text-sm ${code.expires_at ? expiryToneClass(code.expires_at) : "al-text-gray-500"}`}
                >
                  {code.expires_at ? (
                    <span title={dayjs(code.expires_at).format("MMM D, YYYY, h:mm A")}>
                      {dayjs(code.expires_at).fromNow()}
                    </span>
                  ) : (
                    "Never"
                  )}
                </td>
                <td className="al-px-4 al-py-3 al-text-sm al-text-gray-700">{code.created_by}</td>
                <td className="al-px-4 al-py-3 al-text-sm al-text-gray-700">
                  <span title={dayjs(code.created_at).format("MMM D, YYYY, h:mm A")}>
                    {dayjs(code.created_at).fromNow()}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
